import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AuthFacade } from '../../../store/state/auth/auth.facade';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TrackingMapsRenderService } from '../tracking-maps-render.service';
import { LazyRenderService } from '../../../store/services/lazy-render.service';
import type { ShipmentsGlobalMapComponent } from './shipments-global-map/shipments-global-map.component';
import { filter, take } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { IconNames } from '../../../common/constants';
import { ShipmentGlobalTrackingMapModel } from '../../../store/state/monitoring/shipment/models/shipment-list/shipment-global-tracking-map.model';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { MatTooltip } from '@angular/material/tooltip';
import { DirectivesModule } from '../../../common/directives/directives.module';

@Component({
  selector: 'lib-shipments-global-tracking-map',
  imports: [CommonModule, TranslateModule, SvgIconComponent, MatTooltip, DirectivesModule],
  templateUrl: './shipments-global-tracking-map.component.html',
  styleUrls: ['./shipments-global-tracking-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipmentsGlobalTrackingMapComponent {
  @Input() set mapData(mapData: ShipmentGlobalTrackingMapModel[]) {
    if (mapData) {
      if (this.componentRef) {
        this.componentRef.instance.resetState = !this._mapData.length;
        this.componentRef.instance.mapData = mapData;
      } else {
        this.scriptAppendSubscription(mapData);
      }

      if (!this.isMapExpendable) {
        this.isOpened$.next(false);
      }

      this._mapData = mapData;
    }
  }
  @Input() set completelyLoaded(loaded: boolean) {
    this.completelyLoaded$.next(loaded);
  }
  @Input() firstChunkLoaded = false;
  @Input() isMapThresholdReached = false;
  @Input() mapThreshold: number | null = null;
  @Input() totalShipments: number | null = null;
  public authFacade = inject(AuthFacade);

  get isMapExpendable(): boolean {
    return this.totalShipments > 0;
  }

  get tooltipMessage(): string | null {
    if (!this.isMapThresholdReached) {
      return 'monitoring.shipmentsTrackingMap.tooltipThresholdMessage';
    }

    if (!this.isMapExpendable) {
      return 'common.list.noData';
    }

    return null;
  }

  @ViewChild('mapContainer', { read: ViewContainerRef, static: false })
  mapContainerRef: ViewContainerRef;

  iconNames = IconNames;
  isOpened$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  completelyLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _mapData: ShipmentGlobalTrackingMapModel[] = [];
  private componentRef: ComponentRef<ShipmentsGlobalMapComponent> | null = null;

  constructor(
    private trackingMapsRenderService: TrackingMapsRenderService,
    private lazyRenderService: LazyRenderService,
  ) {}

  toggleMap(): void {
    if (!this.isMapExpendable || !this.firstChunkLoaded) {
      return;
    }

    this.isOpened$.next(!this.isOpened$.getValue());
  }

  private scriptAppendSubscription(mapData: ShipmentGlobalTrackingMapModel[]): void {
    this.trackingMapsRenderService.isGoogleMapApiScriptAppended$.pipe(filter(Boolean), take(1)).subscribe(async () => {
      if (this.mapContainerRef) {
        const { ShipmentsGlobalMapComponent } = await import('./shipments-global-map/shipments-global-map.component');

        this.componentRef = this.lazyRenderService.render(this.mapContainerRef, ShipmentsGlobalMapComponent);
        this.componentRef.instance.mapData = mapData;
      }
    });
  }
}
